import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navLinks = [
  { to: '/', label: '首页' },
  { to: '/category', label: '分类' },
  { to: '/search', label: '搜索' },
  { to: '/about', label: '关于' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  void scrolled;

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setSearchOpen(false);
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search');
  };

  const isActive = (to: string) => (to === '/' ? location.pathname === '/' : location.pathname.startsWith(to));

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200"
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-leaf-line text-lg md:text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-bold text-lg md:text-xl text-foreground-950">香草视频</span>
              <span className="text-[10px] md:text-xs text-foreground-500 tracking-wide">在线观看免费播放电视剧</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  isActive(link.to)
                    ? 'text-primary-700 bg-primary-50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <form onSubmit={submitSearch} className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电视剧…"
                className="w-52 lg:w-64 h-10 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
              />
            </form>
          </div>

          <div className="flex md:hidden items-center gap-1">
            <button
              type="button"
              aria-label="搜索"
              onClick={() => setSearchOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-search-line text-xl" />
            </button>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 animate-fade-in">
          <nav className="px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-3 rounded-md text-sm font-medium ${
                  isActive(link.to) ? 'text-primary-700 bg-primary-50' : 'text-foreground-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}

      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-background-50 animate-fade-in">
          <div className="flex items-center gap-2 px-4 h-16 border-b border-background-200">
            <form onSubmit={submitSearch} className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                autoFocus
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入电视剧名称…"
                className="w-full h-11 pl-10 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </form>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              className="text-sm text-foreground-700 px-2 whitespace-nowrap cursor-pointer"
            >
              取消
            </button>
          </div>
        </div>
      )}
    </header>
  );
}